import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Pipeline execution status
 */
export type PipelineStatus =
  | 'idle'
  | 'initializing'
  | 'running'
  | 'failed'
  | 'completed';

/**
 * Pipeline state structure
 */
export interface PipelineState {
  dataFlowId: number | null;
  sessionId: string | null;
  pipelineName: string;
  pipelineDescription: string;
  isRunEnabled: boolean;
  status: PipelineStatus;
}

/**
 * Initial state
 */
const initialState: PipelineState = {
  dataFlowId: null,
  sessionId: null,
  pipelineName: '',
  pipelineDescription: '',
  isRunEnabled: false,
  status: 'idle',
};

/**
 * Slice
 */
const pipelineSlice = createSlice({
  name: 'pipeline',
  initialState,
  reducers: {
    /**
     * Set pipeline identity
     */
    setPipelineIdentity(
      state,
      action: PayloadAction<{
        dataFlowId: number | null;
        sessionId: string | null;
      }>
    ) {
      state.dataFlowId = action.payload.dataFlowId;
      state.sessionId = action.payload.sessionId;
    },

    /**
     * Set pipeline basic details
     */
    setPipelineDetails(
      state,
      action: PayloadAction<{
        pipelineName: string;
        pipelineDescription: string;
      }>
    ) {
      state.pipelineName = action.payload.pipelineName;
      state.pipelineDescription = action.payload.pipelineDescription;
    },

    /**
     * Enable / disable run
     */
    setRunEnabled(state, action: PayloadAction<boolean>) {
      state.isRunEnabled = action.payload;
    },

    /**
     * Set pipeline status
     */
    setStatus(state, action: PayloadAction<PipelineStatus>) {
      state.status = action.payload;
    },

    /**
     * Bulk update pipeline state
     */
    setPipelineState(
      state,
      action: PayloadAction<{
        dataFlowId: number | null;
        sessionId: string | null;
        pipelineName: string;
        pipelineDescription: string;
        isRunEnabled?: boolean;
        status?: PipelineStatus;
      }>
    ) {
      const {
        dataFlowId,
        sessionId,
        pipelineName,
        pipelineDescription,
        isRunEnabled,
        status,
      } = action.payload;

      state.dataFlowId = dataFlowId;
      state.sessionId = sessionId;
      state.pipelineName = pipelineName;
      state.pipelineDescription = pipelineDescription;

      if (typeof isRunEnabled === 'boolean') {
        state.isRunEnabled = isRunEnabled;
      }

      if (status) {
        state.status = status;
      }
    },

    /**
     * Reset entire pipeline state
     */
    resetPipelineState() {
      return initialState;
    },
  },
});

/**
 * Actions
 */
export const {
  setPipelineIdentity,
  setPipelineDetails,
  setRunEnabled,
  setStatus,
  setPipelineState,
  resetPipelineState,
} = pipelineSlice.actions;

/**
 * Reducer
 */
export default pipelineSlice.reducer;